import { MAX_CLIENT_PICTURE_LENGTH, MAX_LOGO_LENGTH } from "@otrack/domain";

const ACCEPTED = ["image/png", "image/jpeg", "image/webp"];
const MAX_SOURCE_BYTES = 10_000_000;

/** The box a picture is fitted inside and the longest data URL it may end up as. */
export type PictureLimits = { width: number; height: number; length: number };
/** The Logo prints about 60px tall; this keeps it sharp at 4x without bloating every Sent Invoice. */
export const LOGO_LIMITS: PictureLimits = { width: 480, height: 240, length: MAX_LOGO_LENGTH };
/** A Client Picture shows at most 44px square; this keeps it sharp at 4x. */
export const CLIENT_PICTURE_LIMITS: PictureLimits = { width: 176, height: 176, length: MAX_CLIENT_PICTURE_LENGTH };

/** Smaller sizes tried, in order, when the first encoding is over the Logo limit. */
const ENCODINGS: Array<[type: string, quality: number | undefined, scale: number]> = [
  ["image/png", undefined, 1], ["image/webp", 0.9, 1], ["image/webp", 0.75, 1], ["image/webp", 0.8, 0.6]
];

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
const toDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("That image could not be read."));
  reader.onerror = () => reject(new Error("That image could not be read."));
  reader.readAsDataURL(blob);
});

/** The size a picture fits to inside the limits' box, never enlarged. */
export function logoSize(width: number, height: number, scale = 1, limits: PictureLimits = LOGO_LIMITS): { width: number; height: number } {
  const fit = Math.min(1, limits.width / width, limits.height / height) * scale;
  return { width: Math.max(1, Math.round(width * fit)), height: Math.max(1, Math.round(height * fit)) };
}

/** Resizes an uploaded picture into a data URL within the limits, or throws a message fit to show the User. */
export async function prepareLogo(file: File, limits: PictureLimits = LOGO_LIMITS): Promise<string> {
  if (!ACCEPTED.includes(file.type)) throw new Error("Use a PNG, JPEG, or WebP image.");
  if (file.size > MAX_SOURCE_BYTES) throw new Error("That image is over 10 MB. Choose a smaller one.");
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) throw new Error("That image could not be read.");
  try {
    for (const [type, quality, scale] of ENCODINGS) {
      const { width, height } = logoSize(bitmap.width, bitmap.height, scale, limits);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("This browser cannot resize images.");
      context.drawImage(bitmap, 0, 0, width, height);
      const blob = await toBlob(canvas, type, quality);
      if (!blob || blob.type !== type) continue;
      const url = await toDataUrl(blob);
      if (url.length <= limits.length) return url;
    }
  } finally {
    bitmap.close();
  }
  throw new Error("That image is too detailed to shrink down. Try a simpler one.");
}
